import { useState } from 'react';
import { Shield, Lock, Smartphone, Key, Eye, EyeOff, Download, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';

export default function SecuritySettings() {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Changement de mot de passe
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwords.newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/settings/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Mot de passe modifié avec succès');
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(data.message || 'Erreur lors du changement de mot de passe');
      }
    } catch (error) {
      toast.error('Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  // Activer 2FA
  const handleEnable2FA = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/setup-2fa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Générer le QR code
        const qrCodeDataUrl = await QRCode.toDataURL(data.data.otpauthUrl);
        setQrCode(qrCodeDataUrl);
        setBackupCodes(data.data.backupCodes || []);
      } else {
        toast.error(data.message || 'Erreur lors de l\'activation du 2FA');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'activation du 2FA');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code 2FA
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ token: verificationCode })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Double authentification activée avec succès');
        setTwoFactorEnabled(true);
        setQrCode('');
        setVerificationCode('');
      } else {
        toast.error(data.message || 'Code de vérification invalide');
      }
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  // Désactiver 2FA
  const handleDisable2FA = async () => {
    if (!confirm('Êtes-vous sûr de vouloir désactiver la double authentification ?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/disable-2fa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Double authentification désactivée');
        setTwoFactorEnabled(false);
      } else {
        toast.error(data.message || 'Erreur lors de la désactivation');
      }
    } catch (error) {
      toast.error('Erreur lors de la désactivation du 2FA');
    } finally {
      setLoading(false);
    }
  };

  // Télécharger les codes de secours
  const downloadBackupCodes = () => {
    const text = `Codes de secours - Double Authentification\n\n${backupCodes.join('\n')}\n\nConservez ces codes dans un endroit sûr.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes-2fa.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Changement de mot de passe */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Changement de mot de passe
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Modifiez votre mot de passe pour sécuriser votre compte
          </p>
        </div>

        <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mot de passe actuel
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwords.currentPassword}
                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                required
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                required
                minLength={8}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmer le nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                required
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Modification...' : 'Modifier le mot de passe'}
            </button>
          </div>
        </form>
      </div>

      {/* Double authentification (2FA) */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Double authentification (2FA)
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Ajoutez une couche de sécurité supplémentaire avec un code à usage unique
              </p>
            </div>
            <div className="flex items-center gap-3">
              {twoFactorEnabled && (
                <span className="flex items-center gap-1 text-sm text-primary-600">
                  <CheckCircle className="w-4 h-4" />
                  Activé
                </span>
              )}
              <button
                onClick={twoFactorEnabled ? handleDisable2FA : handleEnable2FA}
                disabled={loading}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  twoFactorEnabled
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                } disabled:opacity-50`}
              >
                {twoFactorEnabled ? 'Désactiver' : 'Activer le 2FA'}
              </button>
            </div>
          </div>
        </div>

        {qrCode && (
          <div className="p-6">
            <div className="max-w-md mx-auto space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Scannez ce QR code avec votre application d'authentification (Google Authenticator, Authy, etc.)
                </p>
                <img src={qrCode} alt="QR Code 2FA" className="mx-auto w-48 h-48" />
              </div>

              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code de vérification
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-center text-2xl tracking-widest focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  Vérifier et activer
                </button>
              </form>

              {backupCodes.length > 0 && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Key className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-amber-900">Codes de secours</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Conservez ces codes dans un endroit sûr. Ils vous permettront de vous connecter si vous perdez l'accès à votre application d'authentification.
                      </p>
                      <button
                        onClick={downloadBackupCodes}
                        className="mt-3 flex items-center gap-2 text-sm text-amber-700 hover:text-amber-800 font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Télécharger les codes de secours
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
